import React from "react";
import cn from "classnames";
import parse from "html-react-parser";

import { useTranslation } from "@litres-libs/i18n";
import { SearchResponseItemTypesEnum } from "@litres-libs/types";
import { getPersonRole } from "@litres/utils";
import { SmartLink } from "@litres-modules/smart-link";
import {
  formatItemType,
  highlightSearchQuery,
  trackerSpotlightClickItem,
} from "../utils";
import {
  getAuthorImageURL,
  getBookImageURL,
  getAbsoluteUrl,
} from "../../../utils/helpers";
import { Image } from "../../Image/Image";
import { TagIcon } from "../../../../static/icons/TagIcon";
import { GenreIcon } from "../../../../static/icons/GenreIcon";

import svgAuthor from "../../../../static/icons/author.svg";
import { EmptyPublisher } from "../../../../static/icons/EmptyPublisher";

import type { ISmallSearchArt, UnsoldItemProps } from "../types";

import styles from "./UnsoldItem.module.scss";

export const UnsoldItem = (props: UnsoldItemProps): JSX.Element => {
  const {
    url,
    itemType,
    role,
    title,
    id,
    arts,
    isUserAnonymous,
    externalRef,
    searchQuery,
    setLoading,
    smallLogo,
    saveSearchQuery,
    eventData,
    isDesktop = true,
  } = props;

  const { t } = useTranslation();

  const isTripleCover =
    itemType === SearchResponseItemTypesEnum.collection ||
    itemType === SearchResponseItemTypesEnum.tag ||
    itemType === SearchResponseItemTypesEnum.genre ||
    itemType === SearchResponseItemTypesEnum.series ||
    (itemType === SearchResponseItemTypesEnum.publisher && !smallLogo);

  const handleSearchItemClick = (): void => {
    setLoading(true);
    if (!isUserAnonymous && searchQuery) {
      saveSearchQuery(searchQuery);
    }
  };

  const getImageSrc = (): string => {
    if (itemType === SearchResponseItemTypesEnum.person) {
      return getAuthorImageURL(Number(id));
    }
    if (smallLogo) {
      return getAbsoluteUrl(smallLogo);
    }
    return "";
  };

  return (
    <li
      className={cn(styles.wrapper, { [styles.mobileWrapper]: !isDesktop })}
      tabIndex={0}
      key={url}
      onClick={(e: React.MouseEvent): void => {
        handleSearchItemClick();
        trackerSpotlightClickItem(eventData, e, url);
      }}
      ref={externalRef}
    >
      <SmartLink className={styles.item} href={url}>
        <div
          className={cn(styles.coverWrapper, {
            [styles.tripleCoverWrapper]: isTripleCover,
          })}
        >
          {isTripleCover && (
            <span className={styles.multipleCover}>
              {itemType === SearchResponseItemTypesEnum.tag && !arts && (
                <TagIcon />
              )}
              {itemType === SearchResponseItemTypesEnum.genre && <GenreIcon />}
              {itemType === SearchResponseItemTypesEnum.publisher && !arts && (
                <EmptyPublisher />
              )}
              {itemType !== SearchResponseItemTypesEnum.genre &&
                arts?.slice(0, 3).map((art: ISmallSearchArt, index: number) => {
                  const { cover_ratio: coverRatio, id } = art;

                  return (
                    <span
                      className={cn(styles.childCover, {
                        [styles.firstCover]: index === 0 && arts?.length > 1,
                        [styles.secondCover]:
                          (index === 1 && arts?.length > 1) ||
                          (index === 0 && arts?.length === 1),
                        [styles.thirdCover]: index === 2,
                      })}
                      key={id}
                    >
                      <img
                        src={getBookImageURL(id)}
                        style={{
                          width: 30,
                          height: Math.round(30 / Number(coverRatio)),
                        }}
                        alt={title}
                      />
                    </span>
                  );
                })}
            </span>
          )}
          {!isTripleCover && (
            <Image
              src={getImageSrc()}
              className={
                itemType === SearchResponseItemTypesEnum.publisher
                  ? styles.publishingCover
                  : styles.cover
              }
              fallbackSrc={svgAuthor}
              alt={title}
            />
          )}
        </div>
        <div className={styles.details}>
          <div>
            <span className={styles.type}>
              {role
                ? t(getPersonRole(role as string))
                : t(formatItemType(itemType))}
            </span>
          </div>
          <div>
            <span className={styles.title}>
              {searchQuery &&
                title &&
                parse(highlightSearchQuery(searchQuery, title))}
            </span>
          </div>
        </div>
      </SmartLink>
    </li>
  );
};
